import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Camera,
  Eye,
  Radar,
  MapPin,
  CheckCircle,
  ChevronRight,
  Crosshair,
  ScanLine,
} from "lucide-react";

const AerialPhotographyPage = () => {
  const services = [
    {
      icon: Camera,
      title: "Aerial Imaging",
      description: "High-resolution aerial photography for infrastructure, construction, and reconnaissance.",
    },
    {
      icon: Eye,
      title: "ISR Operations",
      description: "Intelligence, Surveillance, and Reconnaissance flight operations for government clients.",
    },
    {
      icon: Radar,
      title: "Site Surveys",
      description: "Comprehensive aerial site surveys for planning, assessment, and documentation.",
    },
    {
      icon: Crosshair,
      title: "Drone Services",
      description: "Government-grade drone operations for specialized aerial data collection.",
    },
  ];

  const capabilities = [
    "Aerial imaging services",
    "Infrastructure photography",
    "Site surveys and mapping",
    "Government-grade drone services",
    "ISR (Intelligence, Surveillance, Reconnaissance)",
    "Data collection and analysis",
    "Construction progress documentation",
    "Environmental monitoring",
  ];

  const applications = [
    { title: "Infrastructure Inspection", description: "Bridges, roads, pipelines, and utility corridors" },
    { title: "Construction Monitoring", description: "Progress documentation and site surveys" },
    { title: "Environmental Assessment", description: "Land use analysis and environmental surveys" },
    { title: "Security & Surveillance", description: "Perimeter monitoring and reconnaissance" },
  ];

  return (
    <div className="min-h-screen" data-testid="aerial-photography-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1640621202549-b813eb0a0720?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="aerial-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              AERIAL SERVICES
            </p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              AERIAL PHOTOGRAPHY & DRONE OPERATIONS
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed mb-8">
              AMR Global Holdings LLC provides government-grade aerial imaging and 
              ISR flight operations. Our veteran teams deliver professional drone 
              services for infrastructure, reconnaissance, and specialized surveys.
            </p>
            <div className="flex items-center gap-4 text-white text-sm">
              <ScanLine className="w-5 h-5 text-[#c5a04a]" />
              <span>ISR Capable Operations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="aerial-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">
              AERIAL SERVICES
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-tactical text-center" data-testid={`aerial-service-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mx-auto mb-6">
                    <service.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities List */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="aerial-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">FULL SPECTRUM</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                AERIAL CAPABILITIES
              </h2>
              <ul className="space-y-4">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`aerial-capability-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1668836733970-9ed7e53cd2ca?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Drone Operations"
                className="rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-[#1a365d]" data-testid="aerial-applications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">APPLICATIONS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">USE CASES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors"
                data-testid={`aerial-application-${index}`}
              >
                <h3 className="font-heading text-lg text-[#c5a04a] mb-2">{app.title}</h3>
                <p className="text-slate-300 text-sm">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="aerial-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Camera className="w-16 h-16 text-[#1a365d] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
            AERIAL IMAGING SUPPORT
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Contact us for aerial photography, drone operations, or ISR requirements. 
            Our veteran teams deliver government-grade aerial services.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-tactical btn-primary text-sm" data-testid="aerial-contact-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="btn-tactical btn-outline text-sm" data-testid="aerial-services-btn">
                All Services
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AerialPhotographyPage;
